import { useEffect, useState } from 'react'
import { fetchRecommendedOffers } from '@/actions/settings/billingActions'
import type { RecommendedOffersFetchResult } from '@/features/compliance/plan-pricing/recommendation.types'
import { useAppDispatch } from '@/utils/typeHelpers'

export const RECOMMENDATION_TIMEOUT_MS = 4000

export type PlanRecommendationState = {
  recommendation: RecommendedOffersFetchResult | null
  settled: boolean
}

/**
 * `settled` flips at the first answer or the timeout, so the exposure event never waits on a
 * slow request; a recommendation arriving later is still stored for the grid to render.
 * Owned by the page shell rather than the choose step: the step unmounts on a step change,
 * and a per-mount fetch would re-send the once-per-page exposure event on re-entry.
 */
export const usePlanRecommendation = (): PlanRecommendationState => {
  const dispatch = useAppDispatch()
  const [state, setState] = useState<PlanRecommendationState>({
    recommendation: null,
    settled: false,
  })

  useEffect(() => {
    let mounted = true
    const receive = (recommendation: RecommendedOffersFetchResult | null) => {
      if (mounted) setState({ recommendation, settled: true })
    }
    const timer = setTimeout(() => {
      if (mounted) {
        setState((current) =>
          current.settled ? current : { recommendation: null, settled: true }
        )
      }
    }, RECOMMENDATION_TIMEOUT_MS)
    fetchRecommendedOffers({ context: 'plan_change_page' })(dispatch)
      .then((result) => receive(result ?? null))
      .catch(() => receive(null))
    return () => {
      mounted = false
      clearTimeout(timer)
    }
  }, [dispatch])

  return state
}
